// components/LoginForm.jsx
import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom"; // or 'next/navigation' for Next.js
import { ArrowLeft } from "lucide-react";
import {
  sendOtp,
  verifyOtp,
  verifyUser,
  createTripByName,
} from "../api/apiService";
import { formatTripDateRange, parseTripInfo } from "../utils/helper.js";
import { useSnackBar } from "../components/snackBar/SnackBarContext"; // adjust the path as needed
import Button from "components/Button";
import Logo from "../assets/icons/ic_logoNew.svg";
import Otp from "../assets/icons/ic_otp.svg";
import AuthContainer from "../components/auth/AuthContainer";

const LoginForm = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackBar();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [searchParams] = useSearchParams();
  const [tripName, setTripName] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [authStep, setAuthStep] = useState("EMAIL");
  const [authBackSignal, setAuthBackSignal] = useState(0);
  const [showVerifyEmailBanner, setShowVerifyEmailBanner] = useState(false);

  useEffect(() => {
    let trip = searchParams.get("trip"); // e.g., 'greece' or 'turkey'

    if (trip == null) {
      trip = sessionStorage.getItem("tripName");
    } else {
      sessionStorage.setItem("tripName", trip);
    }
    setTripName(trip);
  }, [searchParams]);

  const handleEmailChange = (e) => {
    const value = e.target.value.replace(/\s/g, ""); // remove all spaces
    setEmail(value);
  };

  const handleVerifyEmail = async () => {
    if (isVerifying) return; // stop if already verifying
    setEmailError("");

    // Validate first
    if (!email.trim()) {
      setEmailError("Email is required.");
      return;
    } else if (
      !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)
    ) {
      setEmailError("Enter a valid email (e.g., user@example.com)");
      return;
    }

    // Now start verifying
    setIsVerifying(true);
    // Show "loading" snackbar
    showSnackbar("info", "Verifying your email… please wait.");
    try {
      // call createTripByName if tripName is available
      if (tripName) {
        await createTripByName(tripName);
        sessionStorage.setItem("tripName", tripName);
      }
      await verifyUser(email, tripName);

      localStorage.setItem("email", email);
      localStorage.setItem("isNewUser", false);

      const otpRes = await sendOtp(email);
      if (otpRes) {
        showSnackbar("success", `OTP has been sent to your email!`);
      }
      setStep(2);
    } catch (err) {
      console.error("verifyUser error:", err);

      // Case 1: error is array with message
      if (Array.isArray(err) && err.length > 0 && err[0].message) {
        const errorMessage = err[0].message;

        // Special case: email does not exist → register flow
        if (errorMessage.toLowerCase().includes("email does not exist")) {
          localStorage.setItem("email", email);
          localStorage.setItem("isLoggedIn", true);
          localStorage.setItem("isNewUser", true);
          sessionStorage.setItem("tripName", tripName);
          const otpRes = await sendOtp(email);
          if (otpRes) {
            showSnackbar("success", `OTP has been sent to your email!`);
          }
          setStep(2);
        } else {
          setEmailError(errorMessage);
        }
      }

      // Case 2: error inside response.data
      else if (
        err.response &&
        Array.isArray(err.response.data) &&
        err.response.data.length > 0
      ) {
        const errorMessage = err.response.data[0].message;

        // Same special case
        if (
          typeof errorMessage === "string" &&
          errorMessage.toLowerCase().includes("email does not exist")
        ) {
          localStorage.setItem("email", email);
          localStorage.setItem("isLoggedIn", true);
          localStorage.setItem("isNewUser", true);
          sessionStorage.setItem("tripName", tripName);
          const otpRes = await sendOtp(email);
          if (otpRes) {
            showSnackbar("success", `OTP has been sent to your email!`);
          }
          setStep(2);
        } else {
          setEmailError(errorMessage);
        }
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpError("");

    try {
      const response = await verifyOtp(email, otp, tripName);
      localStorage.setItem("isLoggedIn", true);

      const isEuropeTrip = tripName?.startsWith("europe");
      const route = isEuropeTrip ? "/europregister" : "/register";

      navigate(route, {
        state: {
          infoMessage:
            response.participantExistsAnotherTrip === "yes"
              ? "As an existing registered user, your personal information will be seamlessly imported from your profile."
              : null,
        },
      });
      localStorage.setItem("userType", response.personalInfoDTO.isInvited);
      sessionStorage.setItem("tripName", tripName);
    } catch (err) {
      console.error("OTP verification failed:", err);
      // Handle array-based API error (e.g. [{ message: "..." }])
      if (Array.isArray(err) && err.length > 0 && err[0].message) {
        setOtpError(err[0].message); // show API's message in UI
        showSnackbar("error", err[0].message);
      }
      // Handle array inside err.response.data
      else if (
        err.response &&
        Array.isArray(err.response.data) &&
        err.response.data.length > 0
      ) {
        const apiMsg = err.response.data[0].message;
        setOtpError(apiMsg);
        showSnackbar("error", apiMsg);
      }
      // Handle object-based error (like { error: "Invalid credentials", status: 401 })
      else if (err.response && err.response.data && err.response.data.error) {
        const apiMsg =
          err.response.data.error === "Invalid credentials"
            ? "Invalid OTP. Please try again."
            : err.response.data.error;
        setOtpError(apiMsg);
        showSnackbar("error", apiMsg);
      }
    }
  };

  useEffect(() => {
    if (otp.trim().length === 6) {
      handleVerifyOtp();
    } else {
      setOtpError("Enter Your 6 Digit OTP.");
    }
  }, [otp]);

  return (
    <div className="scroll-m-20">
      <div className="bg-custom-image bg-cover bg-center min-h-[100dvh] w-full overflow-y-auto relative">
        {authStep !== "EMAIL" && (
          <div
            className={`fixed ${showVerifyEmailBanner ? "top-[100px]" : "top-4"} sm:top-4 left-4 z-[80] cursor-pointer text-blue-600`}
          >
            <Button
              text="Back"
              onClick={() => setAuthBackSignal((prev) => prev + 1)}
              iconStart={<ArrowLeft size={18} />}
              className="text-buttonColor text-sm font-semibold px-[10px] py-[6px] border border-buttonColor"
              hideTextOnMobile={true}
            />
          </div>
        )}
        <div className="min-h-[100dvh] mx-auto bg-white bg-opacity-95 flex items-start sm:items-center justify-center px-4 py-6 sm:py-0 overflow-y-auto">
          <div className="w-full flex items-start sm:items-center justify-center min-h-[100dvh] sm:min-h-full">
            <div
              className={`max-w-4xl w-full p-4 sm:p-10 rounded-xl text-center h-auto overflow-visible ${
                authStep === "PASSWORD" ? "pt-20 sm:pt-10" : ""
              }`}
            >
              <img
                src={Logo}
                alt="Logo"
                className={`mx-auto w-[50%] sm:w-[20%] ${
                  authStep !== "EMAIL"
                    ? "sticky top-14 z-40 bg-white/95 py-1"
                    : ""
                }`}
              />
              {/* {authStep === "EMAIL" && ( */}
              <div>
                <h3 className="text-2xl text-secondary font-semibold pt-8">
                  {tripName && tripName.includes("greece")
                    ? tripName.split("-")[2] === "06"
                      ? "A Journey Through Biblical Greece"
                      : "A Journey Through Biblical Greece “Study Tour”"
                    : tripName.includes("europe")
                      ? "European Youth Congress 2026 & Reformation Tour"
                      : "Journey Through the Biblical 7 Churches of Revelation in Turkey"}
                </h3>
                <div className="text-sm pt-4 text-primary">
                  {tripName && tripName.includes("greece") ? (
                    tripName.split("-")[2] === "06" ? (
                      <>
                        <span className="text-lg text-secondary pl-1">
                          {formatTripDateRange(parseTripInfo(tripName), 13)}
                        </span>
                        <p className="text-secondary font-medium">
                          Land Tour & Cruise to the Greek Islands
                        </p>
                        {authStep === "EMAIL" ? (
                          <p className="py-4">
                            Walk where{" "}
                            <span className="text-secondary">Paul preached</span>
                            , stand where{" "}
                            <span className="text-secondary">
                              John received Revelation
                            </span>
                            , and witness the places where{" "}
                            <span className="text-secondary">
                              Christianity first took root.
                            </span>
                            Experience a unique blend of{" "}
                            <span className="text-secondary">
                              archaeology, history, and faith
                            </span>
                            —from ancient ruins to the blue waters of the Greek
                            Islands—on this unforgettable{" "}
                            <span className="text-secondary">
                              Land Tour and Mediterranean Cruise.
                            </span>
                          </p>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <span className="text-lg text-secondary pl-1">
                          {formatTripDateRange(parseTripInfo(tripName), 13)}
                        </span>
                        <p className="text-secondary font-medium">
                          Land Tour & Cruise to the Greek Islands
                        </p>
                        {authStep === "EMAIL" ? (
                          <p className="py-4">
                            Walk where{" "}
                            <span className="text-secondary">Paul preached</span>
                            , stand where{" "}
                            <span className="text-secondary">
                              John received Revelation
                            </span>
                            , and explore the places where
                            <span className="text-secondary">
                              {" "}
                              Christianity first took root.
                            </span>{" "}
                            Experience
                            <span className="text-secondary">
                              {" "}
                              archaeology, history, and faith
                            </span>{" "}
                            come together on this unforgettable
                            <span className="text-secondary">
                              {" "}
                              Land Tour and Mediterranean Cruise.
                            </span>
                          </p>
                        ) : null}
                      </>
                    )
                  ) : tripName.includes("europe") ? (
                    ""
                  ) : (
                    tripName && (
                      <>
                        Journey Through the Biblical 7 Churches of Revelation –
                        Turkey{" "}
                        <span className="text-sm text-secondary pl-1">
                          {formatTripDateRange(parseTripInfo(tripName), 8)}
                        </span>{" "}
                        Join us on an inspiring 8-day journey through Turkey,
                        exploring the archaeological remains of the 7 churches
                        addressed by the Apostle John in the Book of Revelation.
                        From the ruins of Ephesus and Pergamon to the ancient
                        cities of Sardis and Laodicea, each site reveals a
                        powerful message that still speaks today.
                      </>
                    )
                  )}
                </div>
              </div>
              {/* )} */}
              <div className="">
                <AuthContainer
                  tripName={tripName}
                  onStepChange={setAuthStep}
                  onVerifyEmailBannerChange={setShowVerifyEmailBanner}
                  backSignal={authBackSignal}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
